import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Pencil, X } from "lucide-react";
import { toast } from "react-hot-toast";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import FormInputField from "@/components/lib/from-input-field";
import { updateCustomer, unbanAddressById } from "@/redux/slice/customer-slice/customer-slice";

const profileSchema = z.object({
  full_name: z.string().min(1, "Full Name is required"),
  email: z.string().email("Valid Email is required"),
  number: z.string().min(1, "Phone Number is required"),
});

const addressSchema = z.object({
  id: z.number().optional(),
  client_uuid: z.string().optional(),
  postal_code: z.string().min(1, "Post Code is required"),
  door_no: z.string().min(1, "Door No is required"),
  address: z.string().min(1, "Address is required"),
  city: z.string().min(1, "City is required"),
  is_active: z.boolean(),
});

const inputClassName = "bg-cblack3";
const addressInputClassName = "bg-[#3A3B3D] border-white/20";

const generateAddressUuid = () =>
  globalThis.crypto?.randomUUID?.() ||
  `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

const createAddressDraft = (overrides = {}) => ({
  id: undefined,
  client_uuid: generateAddressUuid(),
  postal_code: "",
  door_no: "",
  address: "",
  city: "",
  note: "",
  is_active: true,
  ...overrides,
});

const ensureSingleActiveAddress = (list = []) => {
  if (!Array.isArray(list) || list.length === 0) return [];

  let activeFound = false;

  const normalized = list.map((address) => {
    const shouldBeActive = Boolean(address?.is_active) && !activeFound;

    if (shouldBeActive) {
      activeFound = true;
    }

    return {
      ...address,
      is_active: shouldBeActive,
    };
  });

  if (!activeFound) {
    normalized[0] = {
      ...normalized[0],
      is_active: true,
    };
  }

  return normalized;
};

const hasAddressDraftValue = (draft) =>
  Boolean(
    draft?.postal_code?.trim() ||
      draft?.door_no?.trim() ||
      draft?.address?.trim() ||
      draft?.city?.trim() ||
      draft?.note?.trim(),
  );

const mapCustomerAddresses = (customerData) => {
  if (!customerData) return [];

  if (Array.isArray(customerData.addresses) && customerData.addresses.length > 0) {
    return ensureSingleActiveAddress(
      customerData.addresses.map((address) => ({
        id: address?.id,
        client_uuid: generateAddressUuid(),
        postal_code: address?.postal_code || "",
        door_no: address?.door_no || "",
        address: address?.address || address?.address_line_1 || "",
        city: address?.city || "",
        note: address?.note || "",
        is_active: Boolean(address?.is_active),
        is_banned: Boolean(address?.is_banned),
      })),
    );
  }

  if (customerData.active_address) {
    return [
      {
        id: customerData.active_address?.id,
        client_uuid: generateAddressUuid(),
        postal_code: customerData.active_address?.postal_code || "",
        door_no: customerData.active_address?.door_no || "",
        address: customerData.active_address?.address || customerData.active_address?.address_line_1 || "",
        city: customerData.active_address?.city || "",
        note: customerData.active_address?.note || "",
        is_active: true,
        is_banned: Boolean(customerData.active_address?.is_banned),
      },
    ];
  }

  return [];
};

const normalizeAddressForPayload = (address) => ({
  ...(address?.id ? { id: address.id } : {}),
  postal_code: (address?.postal_code || "").trim(),
  door_no: (address?.door_no || "").trim(),
  address: (address?.address || "").trim(),
  city: (address?.city || "").trim(),
  is_active: Boolean(address?.is_active),
});

function ProfileEditDialog({
  open,
  onOpenChange,
  customerData,
  restaurantId,
  customerId,
}) {
  const dispatch = useDispatch();
  const { formLoading } = useSelector((state) => state.customer.customer);

  const form = useForm({
    resolver: zodResolver(profileSchema),
    mode: "onChange",
    defaultValues: {
      full_name: "",
      email: "",
      number: "",
    },
  });

  useEffect(() => {
    if (!open) return;

    form.reset({
      full_name: customerData?.full_name || "",
      email: customerData?.email || "",
      number: customerData?.number || "",
    });
  }, [open, customerData, form]);

  const onSubmit = async (values) => {
    const existingAddresses = ensureSingleActiveAddress(mapCustomerAddresses(customerData));

    const payload = {
      restaurant_id: Number(restaurantId || 1),
      full_name: values.full_name.trim(),
      email: values.email.trim(),
      number: values.number.trim(),
      address: existingAddresses.map(normalizeAddressForPayload),
    };

    try {
      const response = await dispatch(updateCustomer({ customerId, payload })).unwrap();
      toast.success(response?.message || "Customer updated successfully");
      onOpenChange(false);
    } catch (error) {
      toast.error(error || "Failed to update customer");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] bg-cblack4 p-0">
        <div className="py-5 flex flex-col">
          <DialogTitle className="text-lg font-medium text-cwhite px-8">
            Edit Customer Information
          </DialogTitle>
          <Separator className="mt-4" />

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="px-8 pb-8 pt-5 flex flex-col gap-6"
            >
              <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                <FormInputField
                  name="full_name"
                  style={inputClassName}
                  placeholder="Enter Full Name"
                  form={form}
                  label="Full Name"
                  type="text"
                  required
                />

                <FormInputField
                  name="email"
                  style={inputClassName}
                  placeholder="Enter Email Address"
                  form={form}
                  label="Email Address"
                  type="text"
                  required
                />

                <FormInputField
                  name="number"
                  style={inputClassName}
                  placeholder="Enter Phone Number"
                  form={form}
                  label="Phone Number"
                  type="text"
                  required
                />
              </div>

              <Button
                type="submit"
                className="bg-cyellow w-full text-cblack hover:bg-cyellow/95"
                isLoading={formLoading}
              >
                Update
              </Button>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddressEditDialog({
  open,
  onOpenChange,
  customerData,
  restaurantId,
  customerId,
  defaultEditingAddressId = null,
}) {
  const dispatch = useDispatch();
  const { formLoading } = useSelector((state) => state.customer.customer);

  const [addresses, setAddresses] = useState([]);
  const [addressDraft, setAddressDraft] = useState(() => createAddressDraft());
  const [editingAddressUuid, setEditingAddressUuid] = useState(null);
  const [addressErrors, setAddressErrors] = useState({});

  useEffect(() => {
    if (!open) return;

    const initialAddresses = ensureSingleActiveAddress(mapCustomerAddresses(customerData));
    const initialEditingAddress = defaultEditingAddressId
      ? initialAddresses.find((item) => item.id === defaultEditingAddressId)
      : null;

    setAddresses(initialAddresses);
    setAddressDraft(
      initialEditingAddress
        ? { ...initialEditingAddress }
        : createAddressDraft({ is_active: initialAddresses.length === 0 }),
    );
    setEditingAddressUuid(initialEditingAddress?.client_uuid || null);
    setAddressErrors({});
  }, [open, customerData, defaultEditingAddressId]);

  const handleDraftChange = (key, value) => {
    setAddressDraft((prev) => ({ ...prev, [key]: value }));
    setAddressErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validateAddressDraft = () => {
    const parsed = addressSchema.safeParse(addressDraft);
    if (parsed.success) {
      setAddressErrors({});
      return parsed.data;
    }

    const fieldErrors = {};
    parsed.error.issues.forEach((issue) => {
      const key = issue.path[0];
      if (typeof key === "string") {
        fieldErrors[key] = issue.message;
      }
    });

    setAddressErrors(fieldErrors);
    return null;
  };

  const upsertAddress = ({ baseAddresses, draftAddress, targetUuid }) => {
    const resolvedUuid = targetUuid || draftAddress.client_uuid || generateAddressUuid();
    const nextAddress = {
      ...draftAddress,
      client_uuid: resolvedUuid,
    };

    const updatedAddresses = targetUuid
      ? baseAddresses.map((item) =>
          item.client_uuid === targetUuid
            ? {
                ...item,
                ...nextAddress,
              }
            : item,
        )
      : [...baseAddresses, nextAddress];

    if (nextAddress.is_active) {
      return updatedAddresses.map((item) => ({
        ...item,
        is_active: item.client_uuid === resolvedUuid,
      }));
    }

    return ensureSingleActiveAddress(updatedAddresses);
  };

  const handleAddOrUpdateAddress = () => {
    const validatedAddress = validateAddressDraft();
    if (!validatedAddress) return;

    setAddresses((prev) =>
      upsertAddress({
        baseAddresses: prev,
        draftAddress: validatedAddress,
        targetUuid: editingAddressUuid,
      }),
    );

    setAddressDraft(createAddressDraft({ is_active: false }));
    setEditingAddressUuid(null);
    setAddressErrors({});
  };

  const handleEditAddress = (clientUuid) => {
    const selected = addresses.find((item) => item.client_uuid === clientUuid);
    if (!selected) return;

    setAddressDraft({ ...selected });
    setEditingAddressUuid(clientUuid);
    setAddressErrors({});
  };

  const handleRemoveAddress = (clientUuid) => {
    setAddresses((prev) => {
      const filtered = prev.filter((item) => item.client_uuid !== clientUuid);
      return ensureSingleActiveAddress(filtered);
    });

    if (editingAddressUuid === clientUuid) {
      setEditingAddressUuid(null);
      setAddressDraft(createAddressDraft({ is_active: false }));
      setAddressErrors({});
    }
  };

  const handleSubmit = async () => {
    let finalAddresses = [...addresses];

    if (hasAddressDraftValue(addressDraft)) {
      const validatedAddress = validateAddressDraft();
      if (!validatedAddress) return;

      finalAddresses = upsertAddress({
        baseAddresses: finalAddresses,
        draftAddress: validatedAddress,
        targetUuid: editingAddressUuid,
      });
    }

    if (!finalAddresses.length) {
      toast.error("At least one address is required");
      return;
    }

    finalAddresses = ensureSingleActiveAddress(finalAddresses);

    const payload = {
      restaurant_id: Number(restaurantId || 1),
      full_name: customerData?.full_name || "",
      email: customerData?.email || "",
      number: customerData?.number || "",
      address: finalAddresses.map(normalizeAddressForPayload),
    };

    try {
      const response = await dispatch(updateCustomer({ customerId, payload })).unwrap();
      toast.success(response?.message || "Customer address updated successfully");
      onOpenChange(false);
    } catch (error) {
      toast.error(error || "Failed to update address");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[760px] bg-cblack4 p-0 max-h-[90vh] flex flex-col">
        <div className="flex-shrink-0 py-4 px-8 flex items-center justify-between">
          <DialogTitle className="text-lg font-medium text-cwhite">
            {editingAddressUuid || defaultEditingAddressId ? "Edit Customer Address" : "Add Customer Address"}
          </DialogTitle>
        </div>
        <Separator />

        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
          <h3 className="text-base font-medium leading-none text-cwhite">Address</h3>

          <div className="bg-[#2E2F31] px-4 py-5 md:px-6 space-y-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-[88px_1fr_auto] md:items-start md:gap-4">
              <p className="text-base leading-none font-medium text-cwhite">
                Post Code<span className="text-cred">*</span>
              </p>

              <Input
                className={addressInputClassName}
                placeholder="Post code"
                value={addressDraft.postal_code}
                onChange={(e) => handleDraftChange("postal_code", e.target.value)}
                type="text"
              />

              <Button
                type="button"
                className="h-10 bg-[#22C55E] px-4 text-white hover:bg-[#22C55E]/90"
                onClick={handleAddOrUpdateAddress}
              >
                {editingAddressUuid ? "Update Address" : "Another"}
              </Button>

              {addressErrors.postal_code && (
                <p className="text-xs text-cred md:col-start-2">
                  {addressErrors.postal_code}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-[88px_1fr] md:items-start md:gap-4">
              <p className="text-base leading-none font-medium text-cwhite">Door No</p>

              <Input
                className={addressInputClassName}
                placeholder="Door No"
                value={addressDraft.door_no}
                onChange={(e) => handleDraftChange("door_no", e.target.value)}
                type="text"
              />

              {addressErrors.door_no && (
                <p className="text-xs text-cred md:col-start-2">{addressErrors.door_no}</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-[88px_1fr] md:items-start md:gap-4">
              <p className="text-base leading-none font-medium text-cwhite">
                Address<span className="text-cred">*</span>
              </p>

              <div className="space-y-4">
                <Input
                  className={addressInputClassName}
                  placeholder="Address"
                  value={addressDraft.address}
                  onChange={(e) => handleDraftChange("address", e.target.value)}
                  type="text"
                />

                {addressErrors.address && (
                  <p className="text-xs text-cred">{addressErrors.address}</p>
                )}

                <Input
                  className={addressInputClassName}
                  placeholder="City"
                  value={addressDraft.city}
                  onChange={(e) => handleDraftChange("city", e.target.value)}
                  type="text"
                />

                {addressErrors.city && (
                  <p className="text-xs text-cred">{addressErrors.city}</p>
                )}

                <div className="flex items-center justify-between rounded-md border border-white/20 p-3">
                  <Label className="text-cwhite">Is Active</Label>
                  <Switch
                    checked={addressDraft.is_active}
                    onCheckedChange={(value) =>
                      handleDraftChange("is_active", Boolean(value))
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            {addresses.map((address, index) => (
              <div
                key={address.client_uuid || `${address.id || "new"}-${index}`}
                className="rounded-md border border-white/20 bg-cblack3 p-3"
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <h4 className="text-sm font-medium text-cwhite">Address #{index + 1}</h4>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-auto p-0 text-cyellow hover:bg-transparent hover:text-cyellow"
                      onClick={() => handleEditAddress(address.client_uuid)}
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-auto p-0 text-cred hover:bg-transparent hover:text-cred"
                      onClick={() => handleRemoveAddress(address.client_uuid)}
                    >
                      <X size={14} />
                    </Button>
                  </div>
                </div>
                <div className="text-xs text-cwhite2 space-y-1">
                  <p>Post Code: {address.postal_code || "N/A"}</p>
                  <p>Door No: {address.door_no || "N/A"}</p>
                  <p>Address: {address.address || "N/A"}</p>
                  <p>City: {address.city || "N/A"}</p>
                  <p>Note: {address.note || "N/A"}</p>
                  <p>Status: {address.is_active ? "Active" : "Inactive"}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-shrink-0 px-8 py-4 border-t border-white/10 bg-cblack4">
          <Button
            type="button"
            className="bg-cyellow w-full text-cblack hover:bg-cyellow/95"
            isLoading={formLoading}
            onClick={handleSubmit}
          >
            {editingAddressUuid || defaultEditingAddressId ? "Update Address" : "Add Address"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CustomerDetailsTab({ customerId, customerData, loading, restaurantId }) {
  const viewForm = useForm({
    defaultValues: {
      full_name: "",
      email: "",
      number: "",
    },
  });

  const dispatch = useDispatch();
  const { statusUpdateLoading } = useSelector((state) => state.customer.customer);
  const [openProfileDialog, setOpenProfileDialog] = useState(false);
  const [openAddressDialog, setOpenAddressDialog] = useState(false);
  const [addressEditingId, setAddressEditingId] = useState(null);

  const handleUnbanAddress = async (addressId) => {
    if (!addressId) return;
    try {
      const response = await dispatch(unbanAddressById({ addressId, customerId })).unwrap();
      toast.success(response?.message || "Address unbanned successfully");
    } catch (error) {
      toast.error(error || "Failed to unban address");
    }
  };

  useEffect(() => {
    viewForm.reset({
      full_name: customerData?.full_name || "",
      email: customerData?.email || "",
      number: customerData?.number || "",
    });
  }, [customerData, viewForm]);

  const normalizedAddresses = useMemo(
    () => ensureSingleActiveAddress(mapCustomerAddresses(customerData)),
    [customerData],
  );

  if (loading && !customerData) {
    return (
      <div className="space-y-8">
        <Card className="border-white/10 bg-cblack4 p-8">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
          <Skeleton className="mt-6 h-10 w-[120px]" />
        </Card>

        <div>
          <Skeleton className="h-6 w-[120px]" />
          <Card className="mt-4 border-white/20 bg-cblack4 px-6 py-5">
            <div className="space-y-2">
              <Skeleton className="h-5 w-[220px]" />
              <Skeleton className="h-5 w-[220px]" />
              <Skeleton className="h-5 w-[220px]" />
              <Skeleton className="h-5 w-[220px]" />
            </div>
          </Card>
          <Skeleton className="mt-6 h-10 w-[160px]" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {customerData?.is_banned && (
        <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/20 px-4 py-3 rounded-lg text-red-500 font-semibold text-sm">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <span>Banned Customer</span>
        </div>
      )}
      <Card className="border-white/10 bg-cblack4 p-8">
        <Form {...viewForm}>
          <form className="space-y-6">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              <FormInputField
                name="full_name"
                style="bg-cblack3"
                placeholder="Full name"
                form={viewForm}
                label="Full Name"
                type="text"
                disabled
              />
              <FormInputField
                name="email"
                style="bg-cblack3"
                placeholder="Email"
                form={viewForm}
                label="Email"
                type="text"
                disabled
              />
              <FormInputField
                name="number"
                style="bg-cblack3"
                placeholder="Phone Number"
                form={viewForm}
                label="Phone Number"
                type="text"
                disabled
              />
            </div>

            <Button
              type="button"
              className="bg-cgreen text-white hover:bg-cgreen/90"
              onClick={() => setOpenProfileDialog(true)}
            >
              Edit
            </Button>
          </form>
        </Form>
      </Card>

      <div>
        <h3 className="text-xl leading-none font-medium text-cwhite">Address</h3>

        <div className="mt-4 grid grid-cols-1 gap-4">
          {normalizedAddresses.length > 0 ? (
            normalizedAddresses.map((address, index) => (
              <Card
                key={address.client_uuid || `${address.id || "new"}-${index}`}
                className="border-white/20 bg-cblack4 px-6 py-5"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-2 text-xs md:text-sm text-cwhite2">
                    <p>
                      Post Code : <span>{address.postal_code || "N/A"}</span>
                    </p>
                    <p>
                      Door No : <span>{address.door_no || "N/A"}</span>
                    </p>
                    <p>
                      Address : <span>{address.address || "N/A"}</span>
                    </p>
                    <p>
                      City : <span>{address.city || "N/A"}</span>
                    </p>
                    <div className="flex items-center gap-2">
                      <span>Status : {address.is_active ? "Active" : "Inactive"}</span>
                      {address.is_banned && (
                        <span className="bg-red-500/10 text-red-500 border border-red-500/20 text-xs px-2.5 py-0.5 rounded font-bold uppercase tracking-wider">
                          Banned
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 justify-between items-end self-stretch">
                    {address.is_banned && (
                      <Button
                        type="button"
                        className="bg-cred text-white hover:bg-cred/90 h-9 px-4 text-xs font-semibold"
                        onClick={() => handleUnbanAddress(address.id)}
                        isLoading={statusUpdateLoading}
                      >
                        Unban
                      </Button>
                    )}
                    <Button
                      type="button"
                      className="bg-cgreen text-white hover:bg-cgreen/90 h-9 px-4 text-xs font-semibold mt-auto"
                      onClick={() => {
                        setAddressEditingId(address.id || null);
                        setOpenAddressDialog(true);
                      }}
                    >
                      Edit
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <Card className="border-white/20 bg-cblack4 px-6 py-5 text-cwhite2">
              No address found
            </Card>
          )}
        </div>

        <Button
          type="button"
          className="mt-6 bg-cgreen text-white hover:bg-cgreen/90"
          onClick={() => {
            setAddressEditingId(null);
            setOpenAddressDialog(true);
          }}
        >
          Add New Address
        </Button>
      </div>

      <ProfileEditDialog
        open={openProfileDialog}
        onOpenChange={setOpenProfileDialog}
        customerData={customerData}
        restaurantId={restaurantId}
        customerId={customerId}
      />

      <AddressEditDialog
        open={openAddressDialog}
        onOpenChange={setOpenAddressDialog}
        customerData={customerData}
        restaurantId={restaurantId}
        customerId={customerId}
        defaultEditingAddressId={addressEditingId}
      />
    </div>
  );
}

export default CustomerDetailsTab;
